// <emma-chat-input> — main landing-page input.
//
// 1:1 lift from `design-system/components/prompt-card.{css,html}`. Layout:
//
//   .prompt-card (radius 14, soft shadow, container-query)
//     .prompt-card__input-area
//       <textarea class="prompt-card__textarea"> (auto-grow)
//     .prompt-card__toolbar-wrap (relative — thinking overlay sits absolute)
//       .prompt-card__thinking (overlay, opacity 0/1 driven by `thinking` prop)
//       .prompt-card__toolbar
//         .prompt-card__toolbar-left
//           .prompt-card__badges (provider badge row + expandable plate)
//           .prompt-card__divider + .prompt-card__pricing (✓ Live pricing —
//             collapsed state only; hidden while the plate is expanded)
//         .prompt-card__toolbar-right
//           <button class="prompt-card__send"> (collapses to icon, expands with text)
//
// Composition:
//   - Hosts <emma-chat-typing-prompt> internally for placeholder rotation
//   - Receives the provider list from parent <emma-chat> via the
//     `.providers` property binding (single source of truth — root fetches
//     /api/hero/providers once and passes it down)
//
// Events:
//   - emma-input-submit { text, selectedProviders }
//   - emma-input-clear
//   - emma-input-typing-complete (informational; analytics hook)
//
// Auto-grow textarea: RAF-throttled, capped at min(window.innerHeight*0.4, 320).
//
// Thinking overlay: parent flips `thinking` prop true while in-flight; the
// overlay fades in (opacity 1, pointer-events auto) and the toolbar fades
// out — DS pattern, no layout shift.
//
// Border-angle gradient: when `thinking` is true, an internal RAF rotates
// `--border-angle` on :host so the conic-gradient ::before animates around
// the card. (DS reason: @property doesn't work in Shadow DOM; we drive it
// with JS — same approach as monolith audit §8.2.)

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type { Provider } from '../types/index.js';
import { assetUrl } from '../util/asset-url.js';
import { designTokens } from './design-tokens.js';
import { dispatchStopThinking, stopIcon } from './stop-thinking.js';
import { handleTooltipClampEvent } from './tooltip-clamp.js';
import './emma-chat-typing-prompt.js';
import type { EmmaChatTypingPrompt } from './emma-chat-typing-prompt.js';

const TEXTAREA_HEIGHT_VW_RATIO = 0.4;
const TEXTAREA_MAX_PX = 320;

/** Click-shield movement threshold (design-review fix 7 #3) — the pointer
 *  must travel this far from the expansion-time position before clicks on
 *  the revealed provider circles are accepted. Small enough that any real
 *  re-aim clears it; large enough to absorb hand tremor on the spot. */
const PLATE_GUARD_PX = 7;

/** Pure click-shield decision (design-review fix 7 #3): true when the
 *  pointer at (x, y) has moved beyond `thresholdPx` from the recorded
 *  expansion-time `origin` — i.e. the shield may disarm and circle clicks
 *  become deliberate. A null origin means the shield is not armed. */
export function plateGuardCleared(
  origin: { x: number; y: number } | null,
  x: number,
  y: number,
  thresholdPx: number = PLATE_GUARD_PX,
): boolean {
  if (!origin) return true;
  const dx = x - origin.x;
  const dy = y - origin.y;
  return dx * dx + dy * dy > thresholdPx * thresholdPx;
}

@customElement('emma-chat-input')
export class EmmaChatInput extends LitElement {
  /** Disable send button + textarea (parent: state.isSubmitting). */
  @property({ type: Boolean })
  disabled = false;

  /** When true, hides toolbar behind a "Searching providers…" thinking overlay
   *  + drives the rotating gradient-border RAF loop. */
  @property({ type: Boolean })
  thinking = false;

  /** Live message inside the thinking overlay. Updated by parent on every
   *  thinking_progress SSE event. */
  @property({ type: String, attribute: 'thinking-message' })
  thinkingMessage = 'Searching providers…';

  /** Optional prompts for the typing-rotation placeholder. */
  @property({ type: Array })
  prompts: string[] = [
    'emma, compare H100 pricing across all available clouds',
    'emma, set up a GPU training cluster with 8x A100 in eu-west',
    'emma, configure K8s cluster with autoscale from 2 to 16 nodes',
  ];

  /** Provider list rendered as toolbar badges. Owned by the parent
   *  <emma-chat> root which fetches /api/hero/providers once on connect
   *  and passes the list down via `.providers=${...}`. Decorative — when
   *  empty the badge row simply doesn't render. */
  @property({ attribute: false })
  providers: Provider[] = [];

  /** BFF URL prefix, forwarded from the <emma-chat> root. Provider logo
   *  URLs come back from /api/hero/providers as root-relative paths
   *  (`/assets/providers/…`); when the widget is embedded on the portal
   *  those resolve against the portal origin and 404. assetUrl() prefixes
   *  them with apiBase so they load from the BFF. Empty on same-origin
   *  landing — assetUrl leaves the path untouched. */
  @property({ type: String, attribute: 'api-base' })
  apiBase = '';

  // Component-local state (not in Context — input is leaf-level)
  @state() private text = '';
  @state() private rafBorderAngle = 0;
  /** Set true once the user focuses the textarea — halts typing rotation
   *  permanently for the session (matches monolith completeTyping semantics
   *  per audit §8.1). Programmatic focus (e.g. from quick-chip click) does
   *  not flip this. */
  @state() private userFocused = false;
  /** True while the demo prompt rotation is the active value-source.
   *  Drives:
   *   - visibility of the .demo-esc-hint pseudo-button
   *   - whether the Esc key is intercepted (only when demo content is in
   *     the field; once user has typed their own, Esc is browser-default)
   *  Lifecycle:
   *   - true on mount (rotation starts)
   *   - stays true after focus-intercept fills the full current prompt
   *     (textarea still carries demo content)
   *   - false on first trusted keystroke (user modifies → no longer demo)
   *   - false on Esc / pseudo-button click (user dismissed the demo)
   *  Cycling re-enables only on a fresh mount — no auto-restart. */
  @state() private demoActive = true;
  /** True while a chip-driven typewriter playback is in progress (set by
   *  setText, cleared on completion or on user interrupt via
   *  mousedown / focus / first trusted keystroke). Lets snapToFullPrompt
   *  treat playback like the demo rotation for interrupt purposes. */
  @state() private chipTyping = false;

  @query('textarea') private textarea!: HTMLTextAreaElement;
  // `@query` defines a getter — initializer (`= null`) would try to set
  // a readonly property in the constructor and crash. Definite-assignment
  // (`!`) skips the initializer; the getter returns null automatically
  // before the typing-prompt element is rendered into shadow root.
  @query('emma-chat-typing-prompt') private typingPrompt!: EmmaChatTypingPrompt | null;

  /** Reactive mirror of the textarea ref. @query alone returns null on
   *  first render because shadow DOM hasn't committed yet — passing it
   *  straight to <emma-chat-typing-prompt .target=...> would mount the
   *  child with target=null and the rotation would never start (Lit
   *  doesn't re-render the parent just because shadow DOM commits).
   *  Setting this in firstUpdated() forces a second render where the
   *  resolved textarea flows down through normal prop binding, and the
   *  child's updated() picks it up to kick off rotation. */
  @state() private textareaRef: HTMLTextAreaElement | null = null;

  // Internal lifecycle handles
  private rafGrowId: number | null = null;
  private rafBorderId: number | null = null;

  static override styles = [
    designTokens,
    // Lifted 1:1 from design-system/components/prompt-card.css.
    // Inside the widget's Shadow DOM these are private to this element.
    css`
      :host {
        display: block;
      }

      .prompt-card {
        width: 100%;
        background: var(--white);
        border-radius: 14px;
        box-shadow: 0 4px 24px hsla(225, 20%, 60%, 0.12),
          0 0 48px hsla(220, 15%, 85%, 0.3);
        border: 1px solid var(--gray-10);
        overflow: hidden;
        transition: box-shadow var(--duration-normal) var(--easing),
          border-color var(--duration-slow) var(--easing);
        position: relative;
        container-type: inline-size;
      }

      @container (max-width: 520px) {
        .prompt-card__label,
        .prompt-card__divider {
          display: none;
        }
        /* The AI-assistant disclosure (EU AI Act) must survive narrow widths —
           it becomes the only text label; the pricing label stays icon-only. */
        .prompt-card__ai-note .prompt-card__label {
          display: block;
        }
        /* At mobile widths the provider medallions collide with the AI-assistant
           note and the send button (overlapping them into an unreadable clump),
           so the decorative badge row is hidden entirely — the "AI assistant"
           disclosure carries the toolbar-left alone. Descendant selector (0,2,0)
           is deliberate: the base .prompt-card__badges rule below sets
           display:flex and, being later in source order at equal specificity,
           would otherwise win over a flat .prompt-card__badges override here. */
        .prompt-card__toolbar-left .prompt-card__badges {
          display: none;
        }
      }

      .prompt-card:focus-within {
        border-color: var(--gray-15);
      }

      .prompt-card::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 16px;
        background: conic-gradient(
          from var(--border-angle, 0deg),
          hsla(200, 99%, 73%, 1),
          hsla(228, 93%, 58%, 1),
          hsla(265, 95%, 60%, 1),
          hsla(200, 99%, 73%, 1)
        );
        z-index: 0;
        opacity: 0;
        transition: opacity var(--duration-slow) var(--easing);
      }

      .prompt-card.is-thinking {
        border-color: transparent;
        overflow: visible;
      }
      .prompt-card.is-thinking::before {
        opacity: 1;
      }

      .prompt-card > * {
        position: relative;
        z-index: 1;
        background: var(--white);
      }
      .prompt-card > *:first-child {
        border-radius: 14px 14px 0 0;
      }
      .prompt-card > *:last-child {
        border-radius: 0 0 14px 14px;
      }

      .prompt-card__input-area {
        position: relative;
        padding: clamp(0.75rem, 1.5vw, 1rem) clamp(0.875rem, 1.5vw, 1.125rem);
        min-height: 90px;
      }

      .prompt-card__textarea {
        width: 100%;
        min-height: 88px;
        max-height: min(60vh, 420px);
        overflow-x: hidden;
        overflow-y: hidden;
        border: none;
        outline: none;
        resize: none;
        background: transparent;
        box-sizing: border-box;
        font-family: var(--font-sans);
        font-weight: var(--font-medium);
        font-size: clamp(0.75rem, 1.2vw, 0.875rem);
        letter-spacing: 0.5px;
        color: var(--gray-100);
        line-height: var(--leading-md);
        caret-color: var(--gray-100);
      }
      .prompt-card__textarea::placeholder {
        color: var(--gray-40);
      }

      /* Demo Esc hint — small kbd-cap pseudo-button anchored top-right of
         the input area while the typewriter rotation owns the textarea
         value (or after focus-intercept, until the user types their own).
         Click acts as a real Escape press: clears the demo prompt + halts
         rotation + keeps focus in the textarea. Hidden once demoActive
         flips false (user typed / Esc pressed) AND whenever the textarea
         is empty — during the rotation's erase gap the field shows only
         the placeholder, and a "clear" affordance with nothing to clear
         reads as a stray gray pill in the idle input row (design review).
         Fade-out is opacity-only so the pseudo-button doesn't shift
         layout. Sits on top of the textarea via z-index but pointer-events
         disabled when hidden so user can still click through to focus the
         input. */
      .demo-esc-hint {
        position: absolute;
        top: clamp(0.75rem, 1.5vw, 1rem);
        right: clamp(0.875rem, 1.5vw, 1.125rem);
        display: inline-flex;
        align-items: center;
        padding: 4px;
        font-family: var(--font-mono);
        font-size: 9px;
        line-height: 1.4;
        color: var(--gray-40);
        background: var(--gray-05);
        border: 1px solid var(--gray-15);
        border-radius: 4px;
        cursor: pointer;
        user-select: none;
        z-index: 1;
        transition:
          opacity 200ms var(--easing),
          color var(--duration-fast) var(--easing),
          border-color var(--duration-fast) var(--easing),
          background var(--duration-fast) var(--easing);
      }
      .demo-esc-hint:hover {
        color: var(--gray-60);
        border-color: var(--gray-30);
        background: var(--gray-10);
      }
      .demo-esc-hint:focus-visible {
        outline: 2px solid var(--blue-60);
        outline-offset: 2px;
      }
      .demo-esc-hint.is-hidden {
        opacity: 0;
        pointer-events: none;
      }
      @media (prefers-reduced-motion: reduce) {
        .demo-esc-hint {
          transition: none;
        }
      }

      .prompt-card__toolbar-wrap {
        position: relative;
      }

      .prompt-card__thinking {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: clamp(0.5rem, 1vw, 0.625rem) clamp(0.875rem, 1.5vw, 1.125rem);
        position: absolute;
        inset: 0;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--duration-fast) var(--easing);
      }
      .prompt-card__thinking.is-active {
        opacity: 1;
        pointer-events: auto;
      }

      .prompt-card__thinking-dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--blue-60);
        animation: prompt-dot-bounce 1.2s var(--easing) infinite;
      }
      .prompt-card__thinking-dot:nth-child(2) {
        animation-delay: 0.15s;
      }
      .prompt-card__thinking-dot:nth-child(3) {
        animation-delay: 0.3s;
      }
      @keyframes prompt-dot-bounce {
        0%, 80%, 100% {
          transform: scale(0.7);
          opacity: 0.4;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }

      .prompt-card__thinking-text {
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        color: var(--color-text-muted);
      }

      /* Stop control inside the thinking overlay — cancels the in-flight
         search (bubbles emma-stop-thinking to root <emma-chat>). Muted
         bordered text-button, right-aligned in the overlay row. */
      .thinking-stop {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
        padding: 4px 10px;
        background: none;
        border: 1px solid var(--gray-10);
        border-radius: var(--radius-sm);
        color: var(--color-text-muted);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        line-height: 1;
        cursor: pointer;
        transition:
          color var(--duration-fast) var(--easing),
          background var(--duration-fast) var(--easing);
      }
      .thinking-stop svg {
        flex: none;
      }
      @media (hover: hover) {
        .thinking-stop:hover {
          color: var(--color-text);
          background: var(--gray-10);
        }
      }
      /* Idle hardening (design review): the overlay is only FADED
         (opacity 0) while idle — additionally display:none the Stop pill
         so it can never paint in the idle input row (no engine/compositing
         quirk, no a11y-tree entry while invisible). The batch-2 ?disabled
         binding stays as the Tab-safety guard during the fade transition. */
      .prompt-card__thinking:not(.is-active) .thinking-stop {
        display: none;
      }

      .prompt-card__toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: clamp(0.5rem, 1vw, 0.625rem) clamp(0.875rem, 1.5vw, 1.125rem);
        border-top: 1px solid hsla(224, 28%, 92%, 1);
        gap: 0.5rem;
        transition: opacity var(--duration-fast) var(--easing);
      }
      .prompt-card__thinking.is-active ~ .prompt-card__toolbar {
        opacity: 0;
        pointer-events: none;
      }

      .prompt-card__toolbar-left {
        display: flex;
        align-items: center;
        gap: clamp(0.5rem, 1vw, 1rem);
        min-width: 0;
        flex-shrink: 1;
        /* No overflow:hidden here — the badge tooltips pop above the row,
           and the expanded plate wraps instead of overflowing. */
      }
      .prompt-card__toolbar-right {
        display: flex;
        align-items: center;
        gap: clamp(0.5rem, 1vw, 1rem);
        flex-shrink: 0;
      }

      .prompt-card__badges {
        display: flex;
        align-items: center;
        gap: 3px;
        flex-wrap: wrap; /* narrow card + open plate → second row, never clipped */
        min-width: 0;
        flex-shrink: 1;
      }
      .prompt-card__badge {
        width: 28px;
        height: 28px;
        border-radius: var(--radius-full);
        border: 1px solid var(--gray-10);
        background: var(--white);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        position: relative;
        transition: border-color var(--duration-fast) var(--easing);
      }
      @media (hover: hover) {
        .prompt-card__badge:hover {
          border-color: var(--blue-30);
        }
      }
      .prompt-card__badge.is-selected {
        border-color: var(--blue-60);
      }
      .prompt-card__badge svg,
      .prompt-card__badge img {
        width: 16px;
        height: 16px;
      }

      /* Fast CSS tooltip (QA #9) — uniform on every provider circle, for
         both hover (pointer) and focus (keyboard). Always-generated with
         opacity toggling (same pattern as the refine chips) so the JS
         edge-clamp can measure the box, plus the same controlled ~150ms
         delay. */
      .prompt-card__badge[data-tooltip]::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        /* Design-review fix 3 — clamp inside the chat frame (shared
           tooltip-clamp.ts helper sets --emma-tt-max); long names WRAP
           instead of running out of the card as one nowrap strip. */
        max-width: var(--emma-tt-max, 260px);
        width: max-content;
        padding: 4px 8px;
        background: var(--gray-100);
        color: var(--white);
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: var(--font-medium);
        line-height: 1.4;
        border-radius: var(--radius-sm);
        white-space: normal;
        text-align: left;
        pointer-events: none;
        opacity: 0;
        z-index: 100;
        transition: opacity var(--duration-fast) var(--easing) 150ms;
      }
      /* Design-review fix 3 — edge-aware alignment (see tooltip-clamp.ts):
         near the frame's right edge the tooltip right-aligns to the circle,
         mirrored on the left. */
      .prompt-card__badge[data-tooltip][data-tt-align='right']::after {
        left: auto;
        right: 0;
        transform: none;
      }
      .prompt-card__badge[data-tooltip][data-tt-align='left']::after {
        left: 0;
        transform: none;
      }
      .prompt-card__badge[data-tooltip]:hover::after,
      .prompt-card__badge[data-tooltip]:focus-visible::after {
        opacity: 1;
      }
      @media (prefers-reduced-motion: reduce) {
        .prompt-card__badge[data-tooltip]::after {
          transition: none;
        }
      }

      /* QA #1 (design-review rework) — provider plate. The "+N" control sits
         inline at the end of the badge row; hover/focus swaps it for an
         inline run of the remaining provider circles. Everything is normal
         flow, so the reveal can never escape the chat card — at narrow
         widths the row wraps instead.
         Design-review fix 7 #2: NO box surface — the revealed circles must
         look exactly like the default 4 badges (plain circles, no plate
         background/border/shadow). */
      .prompt-card__more {
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: var(--font-semibold);
        color: var(--gray-60);
      }
      .prompt-card__plate {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 3px;
        min-width: 0;
        animation: plate-in var(--duration-fast) var(--easing) both;
      }
      /* Subtle staggered opacity fill — no transform, no overshoot. */
      .prompt-card__plate .prompt-card__badge {
        animation: plate-badge-in var(--duration-normal) var(--easing) both;
        animation-delay: calc(var(--plate-i, 0) * 30ms);
      }
      @keyframes plate-in {
        from {
          opacity: 0;
        }
      }
      @keyframes plate-badge-in {
        from {
          opacity: 0;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        /* Instant reveal — no fade, no stagger. */
        .prompt-card__plate,
        .prompt-card__plate .prompt-card__badge {
          animation: none;
        }
      }

      /* Visible keyboard focus ring on every provider circle. */
      .prompt-card__badge:focus-visible {
        outline: 2px solid var(--blue-60);
        outline-offset: 1px;
      }

      /* "Live pricing" label + divider (design-review fix 7 #1 — restored).
         Rendered in the collapsed state exactly as before the plate rework;
         while the plate is EXPANDED they are not rendered (frees the row for
         the revealed circles) and fade back in on collapse via pricing-in. */
      .prompt-card__divider {
        width: 1px;
        height: 16px;
        background: var(--gray-20);
        flex-shrink: 0;
      }
      .prompt-card__label {
        font-family: var(--font-sans);
        font-weight: var(--font-medium);
        font-size: 0.75rem;
        line-height: 16px;
        letter-spacing: 0.5px;
        color: var(--gray-60);
        white-space: nowrap;
      }
      .prompt-card__pricing {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .prompt-card__pricing svg {
        flex-shrink: 0;
      }
      /* "AI assistant" note — EU AI Act Art. 50(1) disclosure: the user must be
         informed they are interacting with an AI system before first interaction.
         Lives in the widget (not the host page) so every embed inherits it.
         Show/hide follows the same plate rules as the pricing label. */
      .prompt-card__ai-note {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .prompt-card__ai-note svg {
        flex-shrink: 0;
      }
      .prompt-card__divider,
      .prompt-card__pricing,
      .prompt-card__ai-note {
        animation: pricing-in var(--duration-fast) var(--easing) both;
      }
      @keyframes pricing-in {
        from {
          opacity: 0;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .prompt-card__divider,
        .prompt-card__pricing,
        .prompt-card__ai-note {
          animation: none;
        }
      }

      .prompt-card__send {
        min-width: 56px;
        height: 32px;
        border: 1px solid hsla(222, 22%, 24%, 0.5);
        border-radius: var(--radius-sm);
        background: var(--blue-accent);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background var(--duration-fast) var(--easing),
          min-width var(--duration-slow) var(--easing),
          padding var(--duration-slow) var(--easing),
          gap var(--duration-slow) var(--easing);
        flex-shrink: 0;
        overflow: hidden;
        color: var(--white);
        font: inherit;
      }
      .prompt-card__send svg {
        transition: transform var(--duration-normal) var(--easing);
        flex-shrink: 0;
      }
      .prompt-card__send-label {
        font-family: var(--font-sans);
        font-weight: var(--font-medium);
        font-size: var(--text-sm);
        color: var(--white);
        white-space: nowrap;
        transition: opacity var(--duration-normal) var(--easing),
          max-width var(--duration-slow) var(--easing);
        max-width: 0;
        opacity: 0;
        overflow: hidden;
      }
      @media (hover: hover) {
        .prompt-card__send:hover {
          background: var(--blue-80);
        }
        .prompt-card__send:not(.is-expanded):hover svg {
          transform: translateY(-2px);
        }
        .prompt-card__send.is-expanded:hover svg {
          transform: translateX(3px) rotate(90deg);
        }
      }
      .prompt-card__send:active {
        transform: scale(0.95);
      }
      .prompt-card__send:disabled {
        opacity: 0.5;
        pointer-events: none;
      }
      .prompt-card__send.is-expanded {
        min-width: auto;
        padding: 0 14px;
        gap: 6px;
        animation: prompt-send-pulse 1.5s var(--easing) 0.4s infinite;
      }
      .prompt-card__send.is-expanded .prompt-card__send-label {
        max-width: 120px;
        opacity: 1;
      }
      .prompt-card__send.is-expanded svg {
        transform: rotate(90deg);
        width: 14px;
        height: 14px;
      }
      @keyframes prompt-send-pulse {
        0%, 100% { border-color: hsla(222, 22%, 24%, 0.5); }
        50%       { border-color: var(--blue-60); }
      }

      @media (prefers-reduced-motion: reduce) {
        .prompt-card,
        .prompt-card::before,
        .prompt-card__send,
        .prompt-card__thinking,
        .prompt-card__toolbar,
        .prompt-card__send-label,
        .prompt-card__send svg,
        .prompt-card__thinking-dot {
          transition: none;
          animation: none;
        }
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    // Global Esc listener — lets the user dismiss the demo rotation / chip
    // typewriter even when focus is outside the textarea. Registered in the
    // CAPTURE phase so it deterministically runs BEFORE any host-page
    // bubble-phase keydown listener (notably the portal launcher's
    // Esc-to-close) regardless of listener registration order — see
    // handleGlobalKeydown, which stopImmediatePropagation()s ONLY while the
    // typewriter is the active value-source. Once the user has engaged (idle),
    // it no longer intercepts and Esc returns to its host-page default
    // (closing the launcher, dismissing a host modal, etc.).
    window.addEventListener('keydown', this.handleGlobalKeydown, true);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.handleGlobalKeydown, true);
    this.stopBorderRaf();
    if (this.rafGrowId !== null) {
      cancelAnimationFrame(this.rafGrowId);
      this.rafGrowId = null;
    }
  }

  /** Window-level Esc handler (CAPTURE phase). While the typewriter is the
   *  active value-source (demo rotation OR chip-driven playback), a single Esc
   *  belongs to US: stop the typewriter AND consume the event
   *  (stopImmediatePropagation) so it does NOT also reach host-page bubble
   *  listeners — notably the portal launcher's Esc-to-close, which would
   *  otherwise kill the chat on the very keypress the user meant to stop the
   *  typewriter. When the typewriter is idle we do nothing and let Esc
   *  propagate normally (close the launcher / dismiss a host modal). */
  private handleGlobalKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && (this.demoActive || this.chipTyping)) {
      e.stopImmediatePropagation();
      this.handleDemoEscape(e);
    }
  };

  override firstUpdated(): void {
    // Promote the @query getter result into the reactive `textareaRef`
    // state so the next render passes the resolved textarea down to
    // <emma-chat-typing-prompt>. Without this kick the child mounts
    // with target=null and rotation never starts (see textareaRef
    // jsdoc above).
    this.textareaRef = this.textarea ?? null;
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('thinking')) {
      if (this.thinking) this.startBorderRaf();
      else this.stopBorderRaf();
    }
  }

  private startBorderRaf(): void {
    if (this.rafBorderId !== null) return;
    // Skip the rotation under reduced-motion: the conic-gradient falls
    // back to var(--border-angle, 0deg) — a static gradient still
    // signals the thinking state without animated rotation.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    const tick = (): void => {
      this.rafBorderAngle = (this.rafBorderAngle + 1) % 360;
      this.style.setProperty('--border-angle', `${this.rafBorderAngle}deg`);
      this.rafBorderId = requestAnimationFrame(tick);
    };
    this.rafBorderId = requestAnimationFrame(tick);
  }

  private stopBorderRaf(): void {
    if (this.rafBorderId !== null) {
      cancelAnimationFrame(this.rafBorderId);
      this.rafBorderId = null;
    }
    this.style.removeProperty('--border-angle');
  }

  /** Drop the demo content BEFORE the browser applies the user's first real
   *  edit. The typing-prompt writes into the textarea's own `value`
   *  (emma-chat-typing-prompt.ts — `target.value = text.slice(...)`), so
   *  without this the user's question is APPENDED to whatever the rotation
   *  had typed, and that concatenation is what gets submitted: the first
   *  live query of a session went out as "emma, compare H100 pricing across
   *  all available clouds" + the actual question, and the answer matched the
   *  garbage. Esc cleared it, but nobody presses Esc in a text field.
   *
   *  `beforeinput` rather than `input`: at `input` time the browser has
   *  already merged the two strings and the user's own characters can no
   *  longer be told apart from the demo's. Here the field is still the
   *  demo's, so clearing it is unambiguous and the keystroke then lands in
   *  an empty field.
   *
   *  Only trusted events: the rotation's own synthetic events must not
   *  erase the text it is in the middle of typing. */
  private handleBeforeInput = (e: Event): void => {
    if (!e.isTrusted) return;
    this.clearSystemText();
  };

  /** Same for IME input, which starts a composition before any beforeinput
   *  the field would report as an insertion. */
  private handleCompositionStart = (e: Event): void => {
    if (!e.isTrusted) return;
    this.clearSystemText();
  };

  /** Empty the textarea and hand control to the user, when — and only when —
   *  what it currently holds was put there by us (demo rotation or chip
   *  playback). A no-op once the text is the user's own, so it cannot eat
   *  real input on later keystrokes. */
  private clearSystemText(): void {
    if (!this.demoActive && !this.chipTyping) return;
    this.typingPrompt?.stopRotation();
    this.chipTyping = false;
    this.userFocused = true;
    this.demoActive = false;
    if (this.textarea && this.textarea.value !== '') {
      this.textarea.value = '';
      this.text = '';
      this.autoGrow();
    }
  }

  private handleInput = (e: Event): void => {
    const ta = e.target as HTMLTextAreaElement;
    this.text = ta.value;
    this.scheduleAutoGrow();
    // Trusted = real keystroke / paste from the user. Synthetic input
    // events dispatched by emma-chat-typing-prompt come through with
    // isTrusted=false. First trusted edit means the demo prompt is no
    // longer the source — hide the Esc hint and let the browser handle
    // Esc as default.
    if (e.isTrusted) {
      this.demoActive = false;
      // If the user typed during a chip-typewriter playback, kill it and
      // lock further rotation so we don't fight their input.
      if (this.chipTyping) {
        this.typingPrompt?.stopRotation();
        this.chipTyping = false;
        this.userFocused = true;
      }
    }
  };

  /** Mousedown intercept: user is committing to engage with the input.
   *  Snap textarea.value to the current full demo prompt instantly (no
   *  per-char animation) so the user sees the complete prompt to either
   *  send or Esc-clear. Halts rotation permanently. demoActive stays
   *  true — the value is still demo-sourced until the user types. */
  private handleMousedown = (): void => {
    this.snapToFullPrompt();
  };

  /** Tab-into-focus from outside follows the same intent as a mouse
   *  click — user wants to engage. Snap to full prompt. (Programmatic
   *  focus from refine-chip clicks is filtered out by the typing engine
   *  via its own programmaticFocus flag analog — it doesn't dispatch
   *  here because it's not a true focus event from outside.) */
  private handleFocus = (): void => {
    if (!this.userFocused && (this.demoActive || this.chipTyping)) {
      this.snapToFullPrompt();
    } else {
      this.userFocused = true;
    }
  };

  /** Fill textarea with the full current demo prompt instantly + park
   *  caret at end + halt rotation. demoActive remains true so the Esc
   *  hint stays visible until the user either sends or modifies the
   *  text. Idempotent — safe to call multiple times.
   *
   *  `demoActive` guard: once the user has dismissed the demo (via Esc
   *  or by typing their own text), this is a no-op. Without this, a
   *  later mousedown on the textarea would re-fill the prompt that
   *  was just cleared (typingPrompt.currentTarget still holds the
   *  last-active prompt string until the typing engine itself clears
   *  it after erase, but the engine is halted so it never does). */
  private snapToFullPrompt(): void {
    if (!this.demoActive && !this.chipTyping) return;
    if (!this.textarea) return;
    const target = this.typingPrompt?.currentTarget ?? '';
    if (target && this.text !== target) {
      this.textarea.value = target;
      this.text = target;
      // Park caret at end + autosize on the next frame so the textarea's
      // own keypress / focus sequencing doesn't fight us.
      requestAnimationFrame(() => {
        if (!this.textarea) return;
        const len = this.textarea.value.length;
        this.textarea.setSelectionRange(len, len);
        this.autoGrow();
      });
    }
    if (this.chipTyping) {
      // Snap interrupted a chip-prompt typewriter. Finalize like the
      // playOnce completion callback: stop rotation, lock further demo
      // (userFocused), but keep `demoActive=true` so the Esc hint stays
      // visible — the chip prompt is still a system-suggested value, the
      // user just sped it up. They can still Esc to reject it.
      this.typingPrompt?.stopRotation();
      this.chipTyping = false;
      this.userFocused = true;
    } else {
      this.userFocused = true; // halts typing-prompt via the paused gate
    }
  }

  /** Esc / pseudo-button: clear the demo content and put the user in
   *  control. Only intercepted while demoActive=true; once the user
   *  has typed their own text the browser handles Esc (effectively a
   *  no-op for textareas).
   *
   *  Flag-flip ORDER matters: when Esc fires from outside the textarea
   *  (window keydown) and we then call textarea.focus(), the synchronous
   *  focus event re-enters `handleFocus`. If demoActive/userFocused
   *  were still in their pre-Esc state at that moment, handleFocus
   *  would treat the focus as "user just engaged with demo running"
   *  and call snapToFullPrompt, refilling the textarea we just cleared.
   *  Setting both flags first short-circuits handleFocus into its
   *  no-op branch. */
  private handleDemoEscape = (e?: Event): void => {
    if (e) e.preventDefault();
    if (!this.demoActive && !this.chipTyping) return;
    // Hard-halt typing-prompt. The `?paused` gate flips on the next
    // render once we set userFocused=true, but an in-flight setTimeout
    // could still fire a tick or two before that — explicit stop avoids
    // any stray char appearing after the textarea has been cleared.
    this.typingPrompt?.stopRotation();
    this.chipTyping = false;
    this.userFocused = true;
    this.demoActive = false;
    if (this.textarea) {
      this.textarea.value = '';
      this.text = '';
      this.autoGrow();
      this.textarea.focus();
    }
  };

  private scheduleAutoGrow(): void {
    if (this.rafGrowId !== null) return;
    this.rafGrowId = requestAnimationFrame(() => {
      this.rafGrowId = null;
      this.autoGrow();
    });
  }

  private autoGrow(): void {
    if (!this.textarea) return;
    this.textarea.style.height = 'auto';
    const cap = Math.min(window.innerHeight * TEXTAREA_HEIGHT_VW_RATIO, TEXTAREA_MAX_PX);
    this.textarea.style.height = `${Math.min(this.textarea.scrollHeight, cap)}px`;
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    // Esc (stop-typewriter) is owned by the capture-phase window listener
    // (handleGlobalKeydown), which consumes it via stopImmediatePropagation
    // before it can reach this textarea bubble handler — so no Esc branch is
    // needed here (removes the prior duplicate handling).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submit();
    }
  };

  private handleSendClick = (): void => {
    this.submit();
  };

  private submit(): void {
    const trimmed = this.text.trim();
    if (!trimmed || this.disabled) return;
    // selectedProviders kept in detail for backwards-compatibility with
    // the wire contract — always [] now since we no longer track badge
    // selection (matches monolith :2994 dead-code: .is-selected is
    // never set; selectedProviders array stays empty in production).
    this.dispatchEvent(
      new CustomEvent('emma-input-submit', {
        detail: { text: trimmed, selectedProviders: [] },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Public — programmatically fill the textarea with `text`, animated
   *  via the same per-char typewriter the demo rotation uses. Does NOT
   *  submit; the user reviews and clicks Send. Bridge target for
   *  <emma-chat>'s `setInputText` so host pages can wire preset chips
   *  outside the widget without piercing Shadow DOM themselves.
   *
   *  Animation is delegated to <emma-chat-typing-prompt>'s `playOnce`,
   *  which owns the cadence (35ms ± 25ms jitter, matches monolith). The
   *  user can interrupt at any time by clicking, focusing, or typing —
   *  handlers above call snapToFullPrompt / stopRotation accordingly.
   *
   *  Race fallback: if typing-prompt isn't yet mounted (parent calls
   *  setText before first render commits), do an instant fill. */
  setText(text: string): void {
    if (!this.textarea) return;
    const tp = this.typingPrompt;
    if (!tp) {
      this.textarea.value = text;
      this.text = text;
      this.userFocused = true;
      this.demoActive = false;
      this.scheduleAutoGrow();
      this.textarea.focus();
      requestAnimationFrame(() => {
        if (!this.textarea) return;
        const len = this.textarea.value.length;
        this.textarea.setSelectionRange(len, len);
      });
      return;
    }
    // Animated path. Crucially we do NOT set userFocused yet — the
    // typing-prompt's `paused` gate is bound to userFocused, and flipping
    // it true here would abort playOnce on the next render. We also do
    // NOT focus the textarea here — synthetic focus would hit
    // handleFocus, and (since chipTyping is now true) snapToFullPrompt
    // would finalize playback before it animates a single char. Focus is
    // moved to finishChipTyping so the user lands in the field after the
    // typewriter completes; mid-playback they can still click/tab in to
    // interrupt early.
    this.chipTyping = true;
    // Re-arm the demo-active flag. Chip-prompts are a system-suggested
    // value-source (just like demo rotation), so the Esc affordance must
    // be available even if the user previously dismissed the demo with
    // Esc — they should still be able to reject *this* chip and type
    // their own prompt. Cleared on first trusted keystroke / Esc.
    this.demoActive = true;
    tp.playOnce(text, () => this.finishChipTyping());
  }

  /** Finalization for setText animated path — invoked by playOnce when
   *  the full string has been typed out. Locks rotation (userFocused),
   *  syncs reactive `text` from textarea (typing-prompt mutated value
   *  directly), focuses + caret-end. Crucially leaves `demoActive=true`:
   *  the chip prompt is still a system-suggested value, so the Esc
   *  affordance stays visible until the user types their own text or
   *  Esc-clears. */
  private finishChipTyping(): void {
    this.chipTyping = false;
    this.userFocused = true;
    if (!this.textarea) return;
    this.text = this.textarea.value;
    this.scheduleAutoGrow();
    this.textarea.focus();
    requestAnimationFrame(() => {
      if (!this.textarea) return;
      const len = this.textarea.value.length;
      this.textarea.setSelectionRange(len, len);
    });
  }

  /** Provider plate open/closed (QA #1, design-review rework). Hover or
   *  focus on the "+N" control expands an inline plate that replaces the
   *  control with the remaining provider circles. Collapses when the
   *  pointer leaves the toolbar, focus leaves the badge row, or on Esc. */
  @state()
  private plateOpen = false;

  /** Guards the "+N" @focus handler while WE focus it programmatically
   *  (Esc return-focus) — otherwise the focus would instantly re-expand
   *  the plate the Esc just collapsed. */
  private suppressMoreFocusOpen = false;

  /** Click-shield origin (design-review fix 7 #3). Hover-expansion reveals a
   *  provider circle UNDER the stationary cursor; a click at that moment was
   *  aimed at "+N", not at the circle — without a shield it inserted the
   *  provider into the query. While non-null, pointer clicks on circles are
   *  swallowed; cleared once the pointer actually MOVES (or presses) beyond
   *  PLATE_GUARD_PX from the expansion-time position — a click seconds later
   *  WITHOUT movement still means "+N", so time alone never clears it.
   *  Keyboard activation (click detail === 0) bypasses the shield — focus
   *  navigation is deliberate. Cleared on every plate collapse. */
  private plateGuardPos: { x: number; y: number } | null = null;

  /** Hover path — expand and arm the click-shield at the pointer position. */
  private openPlate = (e?: PointerEvent): void => {
    if (!this.plateOpen && e && typeof e.clientX === 'number') {
      this.plateGuardPos = { x: e.clientX, y: e.clientY };
    }
    this.plateOpen = true;
  };

  /** Row-level pointermove/pointerdown — disarm the click-shield once the
   *  pointer moved (or a press landed) beyond the threshold: that is a
   *  deliberate aim at a circle, not the leftover "+N" click. pointerdown is
   *  included for touch, where a distant tap may produce no pointermove. */
  private handlePlateGuardPointer = (e: PointerEvent): void => {
    if (!this.plateGuardPos) return;
    if (plateGuardCleared(this.plateGuardPos, e.clientX, e.clientY)) {
      this.plateGuardPos = null;
    }
  };

  /** Focus/click/Enter path — expanding removes the "+N" control from the
   *  DOM, so also move focus onto the first revealed circle to keep the
   *  tab position (and make the expansion perceivable to screen-reader
   *  users). */
  private expandFromMore = (): void => {
    if (this.suppressMoreFocusOpen || this.plateOpen) return;
    this.plateOpen = true;
    void this.updateComplete.then(() => {
      this.renderRoot
        .querySelector<HTMLElement>('.prompt-card__plate .prompt-card__badge')
        ?.focus();
    });
  };

  /** Collapse when focus leaves the badge row entirely. relatedTarget is
   *  null while focus transits through <body> (e.g. right after the "+N"
   *  control is swapped out) — ignore that transient; the toolbar
   *  pointerleave and Esc handlers cover the click-away cases. */
  private handleBadgesFocusOut = (e: FocusEvent): void => {
    if (!this.plateOpen) return;
    const next = e.relatedTarget as Node | null;
    if (!next) return;
    const row = e.currentTarget as HTMLElement;
    if (row.contains(next)) return;
    this.plateOpen = false;
    this.plateGuardPos = null;
  };

  /** Esc collapses the plate and returns focus to the "+N" control
   *  (suppressing its focus-open so the plate stays collapsed). */
  private handleBadgesKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !this.plateOpen) return;
    e.stopPropagation();
    this.plateOpen = false;
    this.plateGuardPos = null;
    void this.updateComplete.then(() => {
      const more = this.renderRoot.querySelector<HTMLElement>('.prompt-card__more');
      if (!more) return;
      this.suppressMoreFocusOpen = true;
      more.focus(); // focus event fires synchronously inside this call
      this.suppressMoreFocusOpen = false;
    });
  };

  /** Owner spec: the plate collapses when the cursor leaves the whole
   *  toolbar area — not just the "+N" control. */
  private handleToolbarPointerLeave = (): void => {
    if (this.plateOpen) this.plateOpen = false;
    this.plateGuardPos = null;
  };

  /** QA #1 (design-review rework) — provider badge row. Collapsed: first 4
   *  provider logos + a "+N" control (the BFF orders providers by explicit
   *  Priority, so the head leads with the hyperscalers; 4-wide head kept per
   *  the DG+NL design decision of 2026-07-03 over the 3-wide variant).
   *  Hover/focus on "+N" swaps it for an inline plate with the remaining
   *  provider circles, so ALL providers are visible without anything leaving
   *  the chat card. With 5 or fewer providers there is nothing meaningful to
   *  hide, so render them flat. */
  private renderProviderBadges() {
    const providers = this.providers;
    if (providers.length === 0) return nothing;
    const VISIBLE = 4;
    if (providers.length <= VISIBLE + 1) {
      return html`<div
        class="prompt-card__badges"
        @pointerover=${handleTooltipClampEvent}
        @focusin=${handleTooltipClampEvent}
      >
        ${providers.map((p) => this.renderBadge(p))}
      </div>`;
    }
    const head = providers.slice(0, VISIBLE);
    const rest = providers.slice(VISIBLE);
    return html`<div
      class="prompt-card__badges"
      @focusout=${this.handleBadgesFocusOut}
      @keydown=${this.handleBadgesKeydown}
      @pointerover=${handleTooltipClampEvent}
      @focusin=${handleTooltipClampEvent}
      @pointermove=${this.handlePlateGuardPointer}
      @pointerdown=${this.handlePlateGuardPointer}
    >
      ${head.map((p) => this.renderBadge(p))}
      ${this.plateOpen
        ? this.renderProviderPlate(rest)
        : this.renderMoreButton(rest.length)}
    </div>`;
  }

  private renderBadge(p: Provider, plateIndex?: number) {
    return html`
      <button
        type="button"
        class="prompt-card__badge"
        style=${plateIndex === undefined ? nothing : `--plate-i:${plateIndex}`}
        data-tooltip="${p.displayName ?? p.cloudType}"
        aria-label="${p.displayName ?? p.cloudType}"
        @click=${(e: MouseEvent) => this.handleBadgeClick(e, p)}
      >
        ${p.logoUrl
          ? html`<img src="${assetUrl(this.apiBase, p.logoUrl)}" alt="" />`
          : html`<span>${p.cloudType.slice(0, 1)}</span>`}
      </button>
    `;
  }

  /** Collapsed-state "+N" control. Only rendered while the plate is closed,
   *  so aria-expanded is statically false; expanding replaces it with the
   *  plate circles (focus is moved onto the first one). */
  private renderMoreButton(n: number) {
    return html`
      <button
        type="button"
        class="prompt-card__badge prompt-card__more"
        aria-label="Show all ${this.providers.length} providers"
        aria-expanded="false"
        @pointerenter=${this.openPlate}
        @click=${this.expandFromMore}
        @focus=${this.expandFromMore}
      >
        +${n}
      </button>
    `;
  }

  /** Expanded state — the remaining providers as one inline plate surface.
   *  Same circular badges (tooltip + aria-label + click-to-insert) as the
   *  head row; --plate-i drives the staggered opacity fill. */
  private renderProviderPlate(rest: Provider[]) {
    return html`
      <div class="prompt-card__plate" role="group" aria-label="All providers">
        ${rest.map((p, i) => this.renderBadge(p, i))}
      </div>
    `;
  }

  /** Provider-circle click behind the click-shield (fix 7 #3): a pointer
   *  click (detail > 0) is swallowed while the shield is armed — the circle
   *  appeared under a stationary cursor mid-aim at "+N". Swallowing does
   *  nothing at all (no insert, no collapse — the plate stays open, the
   *  user just moves the pointer and clicks again). Keyboard activation
   *  (detail === 0) always passes. */
  private handleBadgeClick(e: MouseEvent, p: Provider): void {
    if (this.plateGuardPos && e.detail > 0) return;
    this.toggleProvider(p.shortName ?? p.cloudType);
  }

  /** Provider badge click — append " on Provider" to the textarea
   *  (mirrors monolith index.html :2694-2708). No selection state
   *  toggling — monolith's `.is-selected` exists in CSS but is never
   *  applied at runtime. */
  private toggleProvider(name: string): void {
    if (!this.textarea) return;
    const trimmed = this.textarea.value.trimEnd();
    const next = trimmed + (trimmed ? ' on ' : '') + name;
    this.textarea.value = next;
    this.text = next;
    this.scheduleAutoGrow();
    this.textarea.focus();
    this.textarea.setSelectionRange(next.length, next.length);
  }

  /** Stop button inside the thinking overlay — cancel the in-flight
   *  search. Bubbles emma-stop-thinking to root <emma-chat>, which
   *  aborts the SSE and clears the submitting state; the typed query
   *  stays in the textarea, ready to edit/resend. */
  private handleStopClick = (): void => {
    dispatchStopThinking(this);
  };

  override render() {
    const hasText = this.text.trim().length > 0;
    return html`
      <div class="prompt-card${this.thinking ? ' is-thinking' : ''}">
        <div class="prompt-card__input-area">
          <textarea
            class="prompt-card__textarea"
            rows="2"
            spellcheck="false"
            ?disabled=${this.disabled}
            placeholder="${this.prompts[0] ?? 'emma, …'}"
            @beforeinput=${this.handleBeforeInput}
            @compositionstart=${this.handleCompositionStart}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
            @focus=${this.handleFocus}
            @mousedown=${this.handleMousedown}
          ></textarea>
          <emma-chat-typing-prompt
            .prompts=${this.prompts}
            .target=${this.textareaRef}
            ?paused=${this.disabled || this.thinking || this.userFocused}
          ></emma-chat-typing-prompt>
          <button
            type="button"
            class="demo-esc-hint${(this.demoActive || this.chipTyping) && hasText
              ? ''
              : ' is-hidden'}"
            aria-label="Stop demo prompt"
            tabindex="-1"
            @click=${() => this.handleDemoEscape()}
          >Esc</button>
        </div>

        <div class="prompt-card__toolbar-wrap">
          <div class="prompt-card__thinking${this.thinking ? ' is-active' : ''}">
            <span class="prompt-card__thinking-dot"></span>
            <span class="prompt-card__thinking-dot"></span>
            <span class="prompt-card__thinking-dot"></span>
            <span class="prompt-card__thinking-text">${this.thinkingMessage}</span>
            <button
              type="button"
              class="thinking-stop"
              aria-label="Stop search"
              ?disabled=${!this.thinking}
              @click=${this.handleStopClick}
            >
              ${stopIcon()} Stop
            </button>
          </div>

          <div
            class="prompt-card__toolbar"
            @pointerleave=${this.handleToolbarPointerLeave}
          >
            <div class="prompt-card__toolbar-left">
              ${!this.plateOpen
                ? html`<div class="prompt-card__ai-note">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M5.508 1.407a.5.5 0 0 1 .984 0l.525 2.779a1.5 1.5 0 0 0 1.17 1.17l2.779.526a.5.5 0 0 1 0 .983l-2.779.526a1.5 1.5 0 0 0-1.17 1.17l-.525 2.779a.5.5 0 0 1-.984 0l-.525-2.779a1.5 1.5 0 0 0-1.17-1.17l-2.779-.526a.5.5 0 0 1 0-.983l2.779-.526a1.5 1.5 0 0 0 1.17-1.17l.525-2.779z"
                          stroke="#306CF7"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <span class="prompt-card__label">AI assistant</span>
                    </div>
                    <span class="prompt-card__divider"></span>`
                : nothing}
              ${this.renderProviderBadges()}
              ${!this.plateOpen
                ? html`<div class="prompt-card__pricing">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.333 4L6 11.333 2.667 8"
                        stroke="#01A957"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span class="prompt-card__label">Live pricing</span>
                  </div>`
                : nothing}
            </div>
            <div class="prompt-card__toolbar-right">
              <button
                class="prompt-card__send${hasText ? ' is-expanded' : ''}"
                ?disabled=${!hasText || this.disabled}
                aria-label="Send command"
                @click=${this.handleSendClick}
              >
                <span class="prompt-card__send-label">Send request</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3.333 8L8 3.333 12.667 8"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 12.667V3.333"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-input': EmmaChatInput;
  }
}
